import {
  computePlatformFeeEur,
  DEFAULT_TICKET_PRICE,
  DEFAULT_TICKET_TAX,
  MIN_TICKET_TAX,
  organizerShareAfterPlatformFeeEur,
  parseTicketFeaturesString,
} from 'event/ticketPrices'
import type {EventModel} from 'event/models/Event'
import EventPage from 'organization/Event/EventPage'
import {EventFaqHelp} from 'organization/Event/Help/FAQ'
import {useOrganizationEvent} from 'organization/Event/OrganizationEventProvider'
import {useOrganizationEventRoutes} from 'organization/OrganizationRoutes'
import BreadcrumMenu from 'organization/theme/Breadcrum'
import React, {useEffect, useMemo, useState} from 'react'
import {LocalLink} from 'i18n/LocalLink'
import {toast} from 'react-toastify'
import styled from 'styled-components'
import {useI18n} from 'i18n/I18nContext'

const TICKET_PRICE_SLIDER_MAX_EUR = 500

type LocalTicketRow = {
  id?: number
  name: string
  price: number
  ticket_features: string[]
}

function rowsFromEvent(event: EventModel | null): LocalTicketRow[] {
  if (!event) {
    return [
      {
        name: '',
        price: DEFAULT_TICKET_PRICE,
        ticket_features: [''],
      },
    ]
  }
  if (event.tickets?.length) {
    return event.tickets.map((t) => {
      const feats = parseTicketFeaturesString(t.ticket_features)
      return {
        id: t.id,
        name: t.name,
        price: t.price,
        ticket_features: feats.length ? feats : [''],
      }
    })
  }
  const legacy = parseTicketFeaturesString(event.ticket_features)
  return [
    {
      name: event.ticket_name || '',
      price: event.price,
      ticket_features: legacy.length ? legacy : [''],
    },
  ]
}

export const Tickets = () => {
  const {t} = useI18n()
  const {event, syncEventTickets, loading} = useOrganizationEvent()
  const routes = useOrganizationEventRoutes()

  const ticketsSig = useMemo(() => {
    if (!event) return ''
    if (event.tickets?.length) {
      return event.tickets
        .map((x) => `${x.id}:${x.price}:${x.name}:${x.sort_order}`)
        .join('|')
    }
    return `legacy:${event.price}:${event.ticket_name}`
  }, [event])

  const ticketFeeSnapshot = useMemo(() => {
    const e = event?.organization?.economy
    if (!e) return null
    return {
      ticket_fee_percent: e.ticket_fee_percent,
      min_ticket_fee: e.min_ticket_fee,
    }
  }, [event])

  const [rows, setRows] = useState<LocalTicketRow[]>([])

  useEffect(() => {
    if (!event) return
    setRows(rowsFromEvent(event))
  }, [event, ticketsSig])

  const updateRow = (index: number, patch: Partial<LocalTicketRow>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? {...row, ...patch} : row)),
    )
  }

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {name: '', price: DEFAULT_TICKET_PRICE, ticket_features: ['']},
    ])
  }

  const removeRow = (index: number) => {
    setRows((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    )
  }

  const moveRow = (index: number, dir: -1 | 1) => {
    setRows((prev) => {
      const next = index + dir
      if (next < 0 || next >= prev.length) return prev
      const copy = [...prev]
      const tmp = copy[index]
      copy[index] = copy[next]
      copy[next] = tmp
      return copy
    })
  }

  const addFeatureToRow = (rowIndex: number) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === rowIndex
          ? {...row, ticket_features: [...row.ticket_features, '']}
          : row,
      ),
    )
  }

  const updateFeature = (
    rowIndex: number,
    featureKey: number,
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === rowIndex
          ? {
              ...row,
              ticket_features: row.ticket_features.map((f, j) =>
                j === featureKey ? value : f,
              ),
            }
          : row,
      ),
    )
  }

  const handleSave = async () => {
    for (let i = 0; i < rows.length; i++) {
      const name = rows[i].name.trim()
      if (!name) {
        toast.warning(t('orgTickets.nameRequired'))
        return
      }
      if (name.length > 10) {
        toast.warning(t('orgTickets.nameTooLong'))
        return
      }
    }

    const payload = rows.map((r, i) => ({
      id: r.id,
      name: r.name.trim(),
      price: Number.isFinite(r.price) ? Math.max(0, r.price) : 0,
      ticket_features: r.ticket_features.filter((f) => f.trim()),
      sort_order: i,
    }))

    const ok = await syncEventTickets(payload)
    if (ok) {
      toast.success(t('settings.saved'))
    }
  }

  return (
    <EventPage>
      <>
        <div className="row pl-30 pb-30 pr-30">
          <BreadcrumMenu
            title={t('orgTickets.breadcrumb')}
            helpInfo={<EventFaqHelp faqKey="ticketsPage" />}
          />

          <div className="col-xxl-12">
            <div className="create__event-area">
              <div className="body__card-wrapper">
                <CapacityHolder>
                  <div>
                    <p style={{fontSize: '1.5rem'}}>
                      {t('orgTickets.capacity')} <b>{event?.total_seats}</b>
                    </p>
                    <p style={{fontSize: '1rem'}}>
                      {t('orgTickets.occupied')}{' '}
                      <b>
                        {' '}
                        {(event?.total_seats ?? 0) -
                          (event?.available_seats ?? 0)}
                      </b>
                    </p>
                    <p style={{fontSize: '1rem'}}>
                      {t('orgTickets.free')} <b>{event?.available_seats}</b>
                    </p>
                    {t('orgTickets.addonHint')}
                  </div>
                  <LocalLink to={routes.addons} className="input__btn w-auto">
                    {t('orgTickets.buyAddon')}
                  </LocalLink>
                </CapacityHolder>

                <p
                  className="mb-20"
                  style={{fontSize: '1.1rem', fontWeight: 600}}
                >
                  {t('orgTickets.ticketTypesTitle')}
                </p>

                {rows.map((row, rowIndex) => {
                  const facePriceEur = Number.isFinite(row.price)
                    ? Math.max(0, row.price)
                    : 0
                  const organizerShareEur =
                    organizerShareAfterPlatformFeeEur(
                      facePriceEur,
                      ticketFeeSnapshot,
                    )
                  const platformFeeEur = computePlatformFeeEur(
                    facePriceEur,
                    ticketFeeSnapshot,
                  )
                  const sliderMaxEur = Math.min(
                    5000,
                    Math.max(
                      TICKET_PRICE_SLIDER_MAX_EUR,
                      Math.ceil(facePriceEur),
                    ),
                  )

                  return (
                    <TicketTypeCard key={rowIndex}>
                      <TicketTypeCardHeader>
                        <span>
                          {t('orgTickets.ticketTypeLabel')} {rowIndex + 1}
                        </span>
                        <TicketTypeCardActions>
                          <button
                            type="button"
                            className="unfield__input-btn"
                            disabled={rowIndex === 0}
                            onClick={() => moveRow(rowIndex, -1)}
                          >
                            {t('orgTickets.moveUp')}
                          </button>
                          <button
                            type="button"
                            className="unfield__input-btn"
                            disabled={rowIndex >= rows.length - 1}
                            onClick={() => moveRow(rowIndex, 1)}
                          >
                            {t('orgTickets.moveDown')}
                          </button>
                          <button
                            type="button"
                            className="unfield__input-btn"
                            disabled={rows.length <= 1}
                            onClick={() => removeRow(rowIndex)}
                          >
                            {t('orgTickets.removeTicketType')}
                          </button>
                        </TicketTypeCardActions>
                      </TicketTypeCardHeader>

                      <div className="create__event-main pt-15">
                        <div className="event__left-box">
                          <div className="create__input-wrapper">
                            <div className="mb-15">
                              <label className="input__field-text">
                                {t('orgTickets.ticketPrice')}{' '}
                                <b>
                                  {facePriceEur === 0
                                    ? t('eventRow.free')
                                    : `${facePriceEur.toFixed(2)} €`}
                                </b>
                              </label>
                              <Row>
                                <PriceRange
                                  type="range"
                                  min="0"
                                  max={sliderMaxEur}
                                  step="1"
                                  value={facePriceEur}
                                  onChange={(e) =>
                                    updateRow(rowIndex, {
                                      price: Number(e.target.value),
                                    })
                                  }
                                />
                                <PriceInput
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={row.price}
                                  onChange={(e) => {
                                    const parsed = parseFloat(e.target.value)
                                    updateRow(rowIndex, {
                                      price: Number.isFinite(parsed)
                                        ? Math.max(0, parsed)
                                        : 0,
                                    })
                                  }}
                                />
                              </Row>
                            </div>
                            <span className="input__alert alert-bg-2 mb-10">
                              {t('orgTickets.suggestedPrice')}{' '}
                              {DEFAULT_TICKET_PRICE} €
                            </span>

                            <div className="singel__input-field mb-15">
                              <label className="input__field-text">
                                {t('orgTickets.ticketName')}{' '}
                                <i>{t('orgTickets.ticketNameHint')}</i>
                              </label>
                              <input
                                maxLength={10}
                                value={row.name}
                                onChange={(e) =>
                                  updateRow(rowIndex, {name: e.target.value})
                                }
                                placeholder={t('orgTickets.ticketNamePh')}
                                required
                              />
                            </div>

                            <div className="singel__input-field mb-15">
                              <label className="input__field-text">
                                {t('orgTickets.features')}
                              </label>
                              {row.ticket_features.map((feature, fIndex) => (
                                <input
                                  key={fIndex}
                                  className="mb-20"
                                  placeholder={t('orgTickets.featurePh')}
                                  value={feature}
                                  onChange={(e) =>
                                    updateFeature(
                                      rowIndex,
                                      fIndex,
                                      e.target.value,
                                    )
                                  }
                                />
                              ))}
                              <button
                                type="button"
                                className="unfield__input-btn w-100 mb-20"
                                onClick={() => addFeatureToRow(rowIndex)}
                              >
                                <i className="fa-regular fa-plus"></i>
                                {t('orgTickets.addFeature')}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="event__right-box">
                          <div className="singel__input-field mb-15">
                            <TotalPriceHolder>
                              <div>
                                <i>{t('orgTickets.yourShare')}</i>
                                <br />
                                {organizerShareEur < 0
                                  ? 0
                                  : organizerShareEur.toFixed(2)}{' '}
                                €
                              </div>
                              <div>+</div>
                              <div>
                                <i>{t('orgTickets.platformFee')}</i>
                                <span>{platformFeeEur.toFixed(2)} €</span>
                                <span style={{fontSize: 15, display: 'block'}}>
                                  <i>
                                    {t('orgTickets.platformFeeNote')
                                      .replace(
                                        '{pct}',
                                        String(
                                          ticketFeeSnapshot?.ticket_fee_percent ??
                                            DEFAULT_TICKET_TAX,
                                        ),
                                      )
                                      .replace(
                                        '{min}',
                                        String(
                                          ticketFeeSnapshot?.min_ticket_fee ??
                                            MIN_TICKET_TAX,
                                        ),
                                      )}
                                  </i>
                                </span>
                              </div>
                              <div>=</div>
                              <TotalPrice>
                                <i>{t('orgTickets.finalPrice')}</i>
                                <br />
                                {facePriceEur === 0
                                  ? t('eventRow.free')
                                  : `${facePriceEur.toFixed(2)} €`}
                              </TotalPrice>
                            </TotalPriceHolder>
                          </div>
                        </div>
                      </div>
                    </TicketTypeCard>
                  )
                })}

                <button
                  type="button"
                  className="unfield__input-btn w-100 mb-20"
                  onClick={addRow}
                >
                  <i className="fa-regular fa-plus"></i>
                  {t('orgTickets.addTicketType')}
                </button>

                <button
                  type="button"
                  disabled={loading}
                  onClick={handleSave}
                  className={
                    loading
                      ? 'disableBtnStyle w-100 mt-10'
                      : 'input__btn w-100 mt-10'
                  }
                >
                  {t('orgTickets.saveTicketTypes')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    </EventPage>
  )
}

const TicketTypeCard = styled.div`
  border: 1px solid var(--clr-border-1, #ddd);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  background: var(--clr-bg-1, #fafafa);
`

const TicketTypeCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
  font-weight: 600;
`

const TicketTypeCardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const TotalPriceHolder = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1.8rem;
  padding: 35px 0px;

  & > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  & > div > i {
    display: block;
    font-size: 1rem;
    font-style: normal;
  }
`

const TotalPrice = styled.div`
  color: #70d640;
  font-weight: bold;
`

const CapacityHolder = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--clr-border-1);
  padding-bottom: 40px;
  margin-bottom: 20px;
`
export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`

export const PriceRange = styled.input`
  flex: 1;
  height: 6px;
  appearance: none;
  background: linear-gradient(90deg, #ff007a, #ffae00);
  border-radius: 4px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #ff007a;
    box-shadow: 0 0 8px rgba(255, 0, 122, 0.5);
    transition: 0.2s;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }
`

export const PriceInput = styled.input`
  height: 55px;
  width: 80px;
  text-align: center;
  padding: 6px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #ccc;
  transition: 0.2s;
  outline: none;

  &:focus {
    border-color: #ff007a;
    box-shadow: 0 0 5px rgba(255, 0, 122, 0.4);
  }
`
